import React, { useState, useEffect, useRef } from 'react';
import './CustomTimePicker.css';

const ITEM_HEIGHT = 52;

const CustomTimePicker = ({ selectedTime, onTimeSelect, onCancel, minTime }) => {
  const parseTime = (timeString) => {
    const [h, m, s] = String(timeString || '').split(':');
    const hour24 = parseInt(h, 10) || 12;
    const minute = parseInt(m, 10) || 0;
    const second = parseInt(s, 10) || 0;

    let hour12 = hour24;
    let period = 'AM';

    if (hour24 === 0) {
      hour12 = 12;
      period = 'AM';
    } else if (hour24 === 12) {
      hour12 = 12;
      period = 'PM';
    } else if (hour24 > 12) {
      hour12 = hour24 - 12;
      period = 'PM';
    } else {
      hour12 = hour24;
      period = 'AM';
    }

    return { hour12, minute, second, period };
  };

  const initialTime = parseTime(selectedTime);
  const [hours, setHours] = useState(initialTime.hour12);
  const [minutes, setMinutes] = useState(initialTime.minute);
  const [seconds, setSeconds] = useState(initialTime.second);
  const [ampm, setAmpm] = useState(initialTime.period);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const secondRef = useRef(null);
  const ampmRef = useRef(null);

  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i);
  const secondOptions = Array.from({ length: 60 }, (_, i) => i);
  const ampmOptions = ['AM', 'PM'];

  const handleSave = () => {
    let hour24 = hours;
    if (ampm === 'PM' && hours !== 12) {
      hour24 = hours + 12;
    } else if (ampm === 'AM' && hours === 12) {
      hour24 = 0;
    }

    const timeString = `${String(hour24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    onTimeSelect(timeString);
  };

  const scrollToValue = (containerRef, value, options) => {
    if (!containerRef.current) {
      return;
    }
    const index = options.indexOf(value);
    if (index === -1) {
      return;
    }
    containerRef.current.scrollTo({
      top: index * ITEM_HEIGHT,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToValue(hourRef, hours, hourOptions);
      scrollToValue(minuteRef, minutes, minuteOptions);
      scrollToValue(secondRef, seconds, secondOptions);
      scrollToValue(ampmRef, ampm, ampmOptions);
    }, 80);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (containerRef, setter, options) => {
    if (!containerRef.current) {
      return;
    }
    const index = Math.round(containerRef.current.scrollTop / ITEM_HEIGHT);
    const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
    const newValue = options[clampedIndex];
    if (newValue !== undefined) {
      setter(newValue);
    }
  };

  const renderTimeOptions = (options, selectedValue, setter, containerRef) => (
    options.map((value) => (
      <button
        type="button"
        key={value}
        className={`time-option ${selectedValue === value ? 'selected' : ''}`}
        onClick={() => {
          setter(value);
          scrollToValue(containerRef, value, options);
        }}
      >
        {typeof value === 'number' ? String(value).padStart(2, '0') : value}
      </button>
    ))
  );

  return (
    <div className="custom-time-picker">
      <button type="button" className="time-picker-back-btn" onClick={onCancel} aria-label="Close time picker">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div className="time-picker-content">
        <div className="time-selector">
          <div className="time-column">
            <div
              className="time-scroll-container"
              ref={hourRef}
              onScroll={() => handleScroll(hourRef, setHours, hourOptions)}
            >
              {renderTimeOptions(hourOptions, hours, setHours, hourRef)}
            </div>
          </div>

          <div className="time-separator" aria-hidden="true">:</div>

          <div className="time-column">
            <div
              className="time-scroll-container"
              ref={minuteRef}
              onScroll={() => handleScroll(minuteRef, setMinutes, minuteOptions)}
            >
              {renderTimeOptions(minuteOptions, minutes, setMinutes, minuteRef)}
            </div>
          </div>

          <div className="time-separator" aria-hidden="true">:</div>

          <div className="time-column">
            <div
              className="time-scroll-container"
              ref={secondRef}
              onScroll={() => handleScroll(secondRef, setSeconds, secondOptions)}
            >
              {renderTimeOptions(secondOptions, seconds, setSeconds, secondRef)}
            </div>
          </div>

          <div className="time-column time-column-period">
            <div
              className="time-scroll-container"
              ref={ampmRef}
              onScroll={() => handleScroll(ampmRef, setAmpm, ampmOptions)}
            >
              {renderTimeOptions(ampmOptions, ampm, setAmpm, ampmRef)}
            </div>
          </div>
        </div>
      </div>

      <div className="time-picker-actions">
        <button type="button" className="time-picker-cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="time-picker-save-btn" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
};

export default CustomTimePicker;
